// Pantheon Visual Intelligence Center — scene renderer.
//
// Three cooperating layers:
//   1. A <canvas> painting the cinematic content (core cluster, filaments,
//      dataflow pulses, depth-fogged connection curves).
//   2. An SVG connection layer used ONLY when canvas 2D is unavailable, so the
//      network metaphor survives without it (also the path jsdom tests take).
//   3. A DOM overlay of real <button> elements for Pantheon and every
//      intelligence — keyboard, touch, focus, and screen-reader interaction
//      never depend on the canvas.
//
// Performance contract: one requestAnimationFrame loop at most; it stops when
// the tab is hidden, the scene is scrolled away, motion is paused, or
// reduced-motion is on (those modes redraw single frames on state change).
// Everything is cleaned up on unmount.

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  advanceScene,
  CAMERA_DISTANCE,
  clampPointToBounds,
  createScene,
  focusScene,
  labelPlacementFor,
  pointOnLink,
  projectPoint,
  qualityForViewport,
  resetSceneFocus,
  safeNodeBounds,
  viewportUnit,
} from "./pantheonSceneModel";

const DPR_CAP = 2;
const EMBED_DPR_CAP = 1.5;
const FALLBACK_VIEWPORT = { width: 960, height: 560 };

const rgbaCache = new Map();
function rgba(hex, alpha) {
  const key = `${hex}|${alpha}`;
  const cached = rgbaCache.get(key);
  if (cached) return cached;
  const value = String(hex || "#889ce7").replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const r = parseInt(full.slice(0, 2), 16) || 136;
  const g = parseInt(full.slice(2, 4), 16) || 156;
  const b = parseInt(full.slice(4, 6), 16) || 231;
  const out = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  rgbaCache.set(key, out);
  return out;
}

// Depth fog tuned to the expanded ring: front-of-ring depth maps to full
// intensity, back-of-ring stays legible instead of vanishing.
function depthAlpha(depth) {
  return Math.max(0.22, Math.min(1, 1.15 - (depth - (CAMERA_DISTANCE - 2)) * 0.16));
}

export default function PantheonNetworkScene({
  viewModel,
  focusedId = null,
  hoveredId = null,
  paused = false,
  reducedMotion = false,
  showLabels = true,
  showCrossLinks = true,
  presentation = "interactive",
  onHover,
  onSelect,
  onRenderMode,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const svgPathRefs = useRef({});
  const buttonRefs = useRef({});
  const sceneRef = useRef(null);
  const ctxRef = useRef(null);
  const renderModeRef = useRef("canvas");
  const rafRef = useRef(0);
  const animationStepRef = useRef(null);
  const lastTsRef = useRef(0);
  const visibleRef = useRef(true);
  const sizeRef = useRef({ ...FALLBACK_VIEWPORT });
  const propsRef = useRef({});
  const isEmbed = presentation === "embed";
  propsRef.current = { hoveredId, focusedId, showCrossLinks, paused, reducedMotion };

  const quality = useMemo(
    () => qualityForViewport({ width: sizeRef.current.width, reducedMotion }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reducedMotion, viewModel]
  );

  // (Re)build the scene when the view model or quality tier changes.
  useEffect(() => {
    sceneRef.current = createScene(viewModel, quality);
    if (focusedId) focusScene(sceneRef.current, focusedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewModel, quality]);

  // Apply focus transitions. In reduced-motion mode the camera snaps instead
  // of animating.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    if (focusedId) focusScene(scene, focusedId);
    else resetSceneFocus(scene);
    if (reducedMotion) {
      if (scene.state.targetYaw !== null) scene.state.yaw = scene.state.targetYaw;
      scene.state.zoom = scene.state.targetZoom;
    }
  }, [focusedId, reducedMotion]);

  // Positioning only. Whether a label exists is decided exclusively by the
  // showLabels prop at render time — this per-frame writer must never touch
  // label visibility, or the two would fall out of sync.
  const positionOverlay = useCallback((projected) => {
    if (isEmbed) return;
    const { width, height } = sizeRef.current;
    Object.entries(projected).forEach(([id, p]) => {
      const el = buttonRefs.current[id];
      if (!el) return;
      el.style.transform = `translate(-50%, -50%) translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px)`;
      el.style.zIndex = String(200 - Math.round(p.depth * 40));
      el.style.opacity = String(Math.max(0.35, Math.min(1, depthAlpha(p.depth) + 0.25)));
      el.dataset.depth = p.depth < CAMERA_DISTANCE ? "near" : "far";
      // Intelligent label offsets: labels push outward from the scene center
      // and flip inward near container edges (see labelPlacementFor). The
      // core label always stays centered beneath the core.
      if (id === "pantheon") {
        el.dataset.labelSide = "center";
        el.dataset.labelVpos = "below";
        return;
      }
      const placement = labelPlacementFor(p, { width, height });
      el.dataset.labelSide = placement.side;
      el.dataset.labelVpos = placement.vpos;
    });
  }, [isEmbed]);

  const drawFrame = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const { width, height } = sizeRef.current;
    const viewport = { width, height };
    const { hoveredId: hover, focusedId: focus, showCrossLinks: cross } = propsRef.current;
    const emphasizedId = hover || (focus && focus !== "pantheon" ? focus : null);
    const state = scene.state;
    const breathe = 1 + Math.sin(state.breathePhase) * 0.045;

    // Project node anchors + core once per frame. Every intelligence anchor is
    // clamped into the shared safe bounds so no node (marker, ring, shells, or
    // its inward-flipped label) can leave the stage — links, canvas drawing,
    // and the DOM overlay all consume the same clamped coordinates.
    const bounds = safeNodeBounds(viewport, { hasControls: !isEmbed });
    const projected = { pantheon: projectPoint({ x: 0, y: 0, z: 0 }, state, viewport) };
    scene.layout.forEach((entry) => {
      projected[entry.id] = clampPointToBounds(
        projectPoint(entry.position, state, viewport),
        bounds
      );
    });

    positionOverlay(projected);

    if (renderModeRef.current === "fallback") {
      // Static SVG connection layer: straight, quadratic-approximated paths.
      scene.primaryLinks.forEach((link) => {
        const el = svgPathRefs.current[`primary-${link.nodeId}`];
        if (!el) return;
        const a = projected.pantheon;
        const b = projected[link.nodeId];
        const c = projectPoint(link.control, state, viewport);
        el.setAttribute("d", `M ${a.x} ${a.y} Q ${c.x} ${c.y} ${b.x} ${b.y}`);
        el.setAttribute("stroke", rgba(link.color, link.nodeId === emphasizedId ? 0.7 : 0.3));
      });
      scene.crossLinks.forEach((link, i) => {
        const el = svgPathRefs.current[`cross-${i}`];
        if (!el) return;
        const a = projected[link.sourceId];
        const b = projected[link.nodeId];
        const c = projectPoint(link.control, state, viewport);
        el.setAttribute("d", `M ${a.x} ${a.y} Q ${c.x} ${c.y} ${b.x} ${b.y}`);
        el.setAttribute("stroke", rgba(link.color, cross ? 0.22 : 0));
      });
      return;
    }

    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);

    // 1 — ambient dust (visualization-only atmosphere).
    ctx.globalCompositeOperation = "source-over";
    scene.dust.forEach((p) => {
      const q = projectPoint(p, state, viewport);
      const tw = 0.4 + 0.6 * Math.abs(Math.sin(state.timeMs * 0.0004 + p.twinklePhase));
      ctx.fillStyle = rgba("#889ce7", 0.16 * tw * depthAlpha(q.depth));
      ctx.beginPath();
      ctx.arc(q.x, q.y, p.size * q.scale * 1.4, 0, Math.PI * 2);
      ctx.fill();
    });

    // 2 — connection curves (primary, then optional cross-agent).
    const drawLink = (link, alpha, dashed) => {
      const a = projected[link.kind === "primary" ? "pantheon" : link.sourceId];
      const b = projected[link.nodeId];
      const c = projectPoint(link.control, state, viewport);
      const gradient = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
      gradient.addColorStop(0, rgba(link.color, alpha * 0.25));
      gradient.addColorStop(1, rgba(link.color, alpha));
      ctx.strokeStyle = gradient;
      ctx.lineWidth = link.kind === "primary" ? 1.1 : 0.8;
      ctx.setLineDash(dashed ? [3, 7] : []);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo(c.x, c.y, b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
    };

    scene.primaryLinks.forEach((link) => {
      const isEmphasized = link.nodeId === emphasizedId;
      const dim = emphasizedId && !isEmphasized ? 0.4 : 1;
      drawLink(link, (isEmphasized ? 0.62 : 0.24) * dim, false);
    });

    if (cross) {
      scene.crossLinks.forEach((link) => {
        const involved =
          !emphasizedId || link.nodeId === emphasizedId || link.sourceId === emphasizedId;
        drawLink(link, involved ? 0.2 : 0.06, true);
      });
    }

    // 3 — traveling dataflow pulses (additive glow).
    ctx.globalCompositeOperation = "lighter";
    state.pulses.forEach((pulse) => {
      const link = scene.primaryLinks[pulse.linkIndex];
      if (!link) return;
      const t = pulse.outbound ? pulse.t : 1 - pulse.t;
      // Pulses ride toward the clamped node anchors, so their glow is clamped
      // into the same bounds to keep arrivals visually attached to the node.
      const q = clampPointToBounds(projectPoint(pointOnLink(link, t), state, viewport), bounds);
      const r = (2.2 + pulse.size * 1.6) * q.scale;
      const glow = ctx.createRadialGradient(q.x, q.y, 0, q.x, q.y, r * 2.4);
      glow.addColorStop(0, rgba(link.color, 0.85 * depthAlpha(q.depth)));
      glow.addColorStop(0.5, rgba(link.color, 0.24));
      glow.addColorStop(1, rgba(link.color, 0));
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(q.x, q.y, r * 2.4, 0, Math.PI * 2);
      ctx.fill();
    });

    // 4 — Pantheon core: halo, filaments, then the living point cluster.
    const coreProj = projected.pantheon;
    const unit = viewportUnit(viewport) * coreProj.scale;
    const coreFlash = state.flashes.pantheon || 0;
    const halo = ctx.createRadialGradient(
      coreProj.x, coreProj.y, 0,
      coreProj.x, coreProj.y, unit * (0.6 + coreFlash * 0.08) * breathe
    );
    halo.addColorStop(0, rgba("#c24de7", 0.36 + coreFlash * 0.2));
    halo.addColorStop(0.45, rgba("#889ce7", 0.17));
    halo.addColorStop(1, rgba("#889ce7", 0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(coreProj.x, coreProj.y, unit * 0.64 * breathe, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalCompositeOperation = "source-over";
    ctx.lineWidth = 0.5;
    scene.core.filaments.forEach(({ a, b }) => {
      const pa = scene.core.points[a];
      const pb = scene.core.points[b];
      const qa = projectPoint(
        { x: pa.x * breathe, y: pa.y * breathe, z: pa.z * breathe }, state, viewport
      );
      const qb = projectPoint(
        { x: pb.x * breathe, y: pb.y * breathe, z: pb.z * breathe }, state, viewport
      );
      ctx.strokeStyle = rgba("#b7c5ff", 0.13 * depthAlpha(qa.depth));
      ctx.beginPath();
      ctx.moveTo(qa.x, qa.y);
      ctx.lineTo(qb.x, qb.y);
      ctx.stroke();
    });

    ctx.globalCompositeOperation = "lighter";
    scene.core.points.forEach((p) => {
      const q = projectPoint(
        { x: p.x * breathe, y: p.y * breathe, z: p.z * breathe }, state, viewport
      );
      const tw = 0.5 + 0.5 * Math.abs(Math.sin(state.timeMs * 0.0007 + p.twinklePhase));
      ctx.fillStyle = rgba("#d9c6ff", (0.5 + coreFlash * 0.3) * tw * depthAlpha(q.depth));
      ctx.beginPath();
      ctx.arc(q.x, q.y, Math.max(0.4, p.size * q.scale * 1.15), 0, Math.PI * 2);
      ctx.fill();
    });

    // 5 — intelligence nodes: orbital ring + layered shell + glow center.
    ctx.globalCompositeOperation = "source-over";
    const drawOrder = [...viewModel.nodes].sort(
      (a, b) => (projected[b.id]?.depth || 0) - (projected[a.id]?.depth || 0)
    );
    drawOrder.forEach((node) => {
      const q = projected[node.id];
      if (!q) return;
      const isEmphasized = node.id === emphasizedId;
      const dim = emphasizedId && !isEmphasized ? 0.5 : 1;
      const flash = state.flashes[node.id] || 0;
      const alpha = depthAlpha(q.depth) * dim;
      const r = Math.max(5, 11 * q.scale) * (isEmphasized ? 1.25 : 1);

      // Orbital ring.
      ctx.strokeStyle = rgba(node.color, 0.4 * alpha);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(q.x, q.y, r * 2.1, r * 0.85, -0.42, 0, Math.PI * 2);
      ctx.stroke();

      // Twin hexagonal shells, counter-rotated for dimensionality.
      for (let shell = 0; shell < 2; shell += 1) {
        const rot = state.timeMs * 0.00012 * (shell === 0 ? 1 : -1) + shell * 0.5;
        const shellR = r * (1.45 + shell * 0.35);
        ctx.strokeStyle = rgba(node.color, (shell === 0 ? 0.5 : 0.28) * alpha);
        ctx.beginPath();
        for (let i = 0; i <= 6; i += 1) {
          const a = rot + (i / 6) * Math.PI * 2;
          const px = q.x + Math.cos(a) * shellR;
          const py = q.y + Math.sin(a) * shellR * 0.88;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
      }

      // Luminous center with receive-flash boost.
      const glowR = r * (1.9 + flash * 1.1);
      const glow = ctx.createRadialGradient(q.x, q.y, 0, q.x, q.y, glowR);
      glow.addColorStop(0, rgba(node.color, (0.95 + flash * 0.05) * alpha));
      glow.addColorStop(0.4, rgba(node.color, 0.35 * alpha));
      glow.addColorStop(1, rgba(node.color, 0));
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(q.x, q.y, glowR, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = rgba("#ffffff", 0.9 * alpha);
      ctx.beginPath();
      ctx.arc(q.x, q.y, Math.max(1.6, r * 0.32), 0, Math.PI * 2);
      ctx.fill();
    });
  }, [viewModel, positionOverlay, isEmbed]);

  // Continuous animation loop — only when motion is allowed and visible.
  useEffect(() => {
    const shouldAnimate = !reducedMotion && !paused && renderModeRef.current === "canvas";

    const step = (ts) => {
      rafRef.current = 0;
      const scene = sceneRef.current;
      if (!scene) {
        return;
      }
      if (renderModeRef.current !== "canvas") {
        // Fallback mode has no animation: paint one static frame and stop.
        rafRef.current = 0;
        drawFrame();
        return;
      }
      if (!visibleRef.current) {
        // Hidden/offscreen means fully paused: do not maintain an idle RAF.
        lastTsRef.current = 0;
        return;
      }
      const dt = lastTsRef.current ? ts - lastTsRef.current : 16;
      lastTsRef.current = ts;
      advanceScene(scene, dt, { paused: false });
      drawFrame();
      rafRef.current = requestAnimationFrame(step);
    };

    animationStepRef.current = shouldAnimate ? step : null;

    if (shouldAnimate && typeof requestAnimationFrame === "function") {
      lastTsRef.current = 0;
      rafRef.current = requestAnimationFrame(step);
    } else {
      // Static modes: settle the camera and paint a single frame.
      const scene = sceneRef.current;
      if (scene && (reducedMotion || paused)) {
        if (scene.state.targetYaw !== null) scene.state.yaw = scene.state.targetYaw;
        scene.state.zoom = scene.state.targetZoom;
      }
      drawFrame();
    }

    return () => {
      if (animationStepRef.current === step) animationStepRef.current = null;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      lastTsRef.current = 0;
    };
    // showLabels is intentionally absent: label visibility is pure React
    // rendering and needs no frame redraw or animation restart.
  }, [reducedMotion, paused, drawFrame, viewModel, focusedId, hoveredId, showCrossLinks]);

  // Canvas bootstrap, sizing, visibility management.
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;

    let ctx = null;
    try {
      ctx = canvas && typeof canvas.getContext === "function" ? canvas.getContext("2d") : null;
    } catch {
      ctx = null;
    }
    ctxRef.current = ctx;
    renderModeRef.current = ctx ? "canvas" : "fallback";
    if (typeof onRenderMode === "function") onRenderMode(renderModeRef.current);

    const resize = () => {
      const width = container?.offsetWidth || FALLBACK_VIEWPORT.width;
      const height = container?.offsetHeight || FALLBACK_VIEWPORT.height;
      sizeRef.current = { width, height };
      const nextQuality = qualityForViewport({
        width,
        reducedMotion: propsRef.current.reducedMotion,
      });
      if (!sceneRef.current || sceneRef.current.quality.key !== nextQuality.key) {
        sceneRef.current = createScene(viewModel, nextQuality);
        if (propsRef.current.focusedId) {
          focusScene(sceneRef.current, propsRef.current.focusedId);
        }
      }
      if (canvas && ctx) {
        const dpr = Math.min(
          typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
          isEmbed ? EMBED_DPR_CAP : DPR_CAP
        );
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      drawFrame();
    };
    resize();

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined" && container) {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(container);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    const updateVisibility = (nextVisible) => {
      visibleRef.current = Boolean(nextVisible);
      if (!visibleRef.current) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        lastTsRef.current = 0;
        return;
      }
      if (!rafRef.current && animationStepRef.current) {
        rafRef.current = requestAnimationFrame(animationStepRef.current);
      } else if (!animationStepRef.current) {
        drawFrame();
      }
    };
    const onVisibility = () => {
      updateVisibility(typeof document === "undefined" || !document.hidden);
    };
    let intersectionObserver = null;
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    if (typeof IntersectionObserver !== "undefined" && container) {
      intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          updateVisibility(
            entry.isIntersecting && (typeof document === "undefined" || !document.hidden)
          );
        });
      });
      intersectionObserver.observe(container);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      else if (typeof window !== "undefined") window.removeEventListener("resize", resize);
      if (intersectionObserver) intersectionObserver.disconnect();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      ctxRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawFrame, isEmbed, onRenderMode]);

  const handleHover = useCallback(
    (id) => {
      if (typeof onHover === "function") onHover(id);
    },
    [onHover]
  );

  const overlayButton = (entry) => {
    const isCore = entry.id === "pantheon";
    const isSelected = focusedId === entry.id;
    const stateText = isCore
      ? viewModel.core.stateLabel
      : `${entry.stateLabel}${entry.readinessLabel ? ` · ${entry.readinessLabel}` : ""}`;
    return (
      <button
        key={entry.id}
        type="button"
        ref={(el) => {
          buttonRefs.current[entry.id] = el;
        }}
        className={`pnc-node ${isCore ? "pnc-node--core" : ""} ${isSelected ? "is-selected" : ""} ${
          hoveredId === entry.id ? "is-hovered" : ""
        }`}
        style={{ "--agent-color": isCore ? "#c24de7" : entry.color }}
        data-testid={`pnc-node-${entry.id}`}
        aria-pressed={isSelected}
        aria-label={
          isCore
            ? `Pantheon — governed intelligence network. ${stateText}`
            : `${entry.name} — ${entry.role}. State: ${stateText}`
        }
        onClick={() => onSelect && onSelect(entry.id)}
        onMouseEnter={() => handleHover(entry.id)}
        onMouseLeave={() => handleHover(null)}
        onFocus={() => handleHover(entry.id)}
        onBlur={() => handleHover(null)}
      >
        <span className="pnc-node-marker" aria-hidden="true" />
        {showLabels && (
          <span className="pnc-node-text" data-testid={`pnc-node-label-${entry.id}`}>
            <span className="pnc-node-name">{isCore ? "PANTHEON" : entry.name}</span>
            {!isCore && <span className="pnc-node-role">{entry.role}</span>}
            <span className={`pnc-node-state pnc-node-state--${isCore ? viewModel.core.stateKey : entry.stateKey}`}>
              {stateText}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="pnc-scene" ref={containerRef} data-testid="pnc-scene">
      <canvas ref={canvasRef} className="pnc-canvas" aria-hidden="true" data-testid="pnc-canvas" />
      {/* Static SVG connection layer — only meaningful when canvas 2D is
          unavailable; kept in the DOM so the fallback swap is seamless. */}
      <svg className="pnc-svg-fallback" aria-hidden="true" data-testid="pnc-svg-fallback">
        {viewModel.nodes.map((node) => (
          <path
            key={`primary-${node.id}`}
            ref={(el) => {
              svgPathRefs.current[`primary-${node.id}`] = el;
            }}
            fill="none"
            strokeWidth="1"
          />
        ))}
        {viewModel.crossLinks.map((link, i) => (
          <path
            key={`cross-${link.from}-${link.to}`}
            ref={(el) => {
              svgPathRefs.current[`cross-${i}`] = el;
            }}
            fill="none"
            strokeWidth="0.8"
            strokeDasharray="3 7"
          />
        ))}
      </svg>
      {!isEmbed ? (
        <div className="pnc-overlay">
          {overlayButton({ id: "pantheon" })}
          {viewModel.nodes.map((node) => overlayButton(node))}
        </div>
      ) : null}
    </div>
  );
}
